import React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    Banner,
    InfoIconWithTooltip,
    LoadingIndicator,
    MandatoryIndicator,
    ValidatingInput,
    ValidatingSelect,
    ValidatingTextArea,
} from '@ygtechnologies/bygr-frontend-component-library';
import { ProjectDeadlineSelect } from '@/seller/components/project/ProjectSettings/ProjectDeadlineSelect';
import { ProjectTagSelect } from '@/seller/components/project/ProjectSettings/ProjectTagSelect';
import { LinkToUnitCompanyProfile } from '@/seller/components/catalog-groups/LinkToUnit/LinkToUnitCompanyProfile';
import { AlertBanner, Checkbox } from '@ygtechnologies/rubus-ui';
import { LinkingType } from '@/types';
import { DirectOptionGroupUpdateParams, RoomOptionGroupUpdateParams } from '@/services/options/option-groups';
import { useQuery } from '@tanstack/react-query';
import { getProjectFeeSets } from '@/services/fees';
import { getOptionItemsByGroup } from '@/services/options/option-items';

export type OptionGroupInfoEditData = Partial<
    Omit<DirectOptionGroupUpdateParams & RoomOptionGroupUpdateParams, 'id' | 'projectId'>
>;

export interface OptionGroupInfoEditProps {
    projectId: number;
    value: OptionGroupInfoEditData;
    /// If group has an Id, we are editing an existing group, otherwise we are creating a new one
    groupId?: number;
    onChange(newValue: OptionGroupInfoEditData): void;
    linkingType: LinkingType;
}

export const OptionGroupInfoEdit: React.FC<OptionGroupInfoEditProps> = ({
    value,
    onChange,
    projectId,
    linkingType,
    groupId,
}) => {
    const handleChange = (changes: Partial<OptionGroupInfoEditData>) => {
        onChange({ ...value, ...changes });
    };
    const feeSetQuery = useQuery({
        queryKey: ['projectFeeSets', projectId],
        queryFn: async () => {
            const data = await getProjectFeeSets(projectId);
            if (value.projectFeeSetId == null && data.length > 0) {
                handleChange({ projectFeeSetId: data[0].id });
            }
            return data;
        },
    });

    const itemsDataQuery = useQuery({
        queryKey: ['getOptionItemsByGroup', groupId],
        queryFn: () => getOptionItemsByGroup(groupId!),
        enabled: !!groupId,
    });

    const disableEditDeadline = React.useMemo(
        () => !!groupId && itemsDataQuery.data?.some((x) => x.optionPresetItems.length > 0),
        [groupId, itemsDataQuery.data]
    );

    if (!feeSetQuery.data) {
        return <LoadingIndicator />;
    }
    return (
        <div className="form columns-1">
            <label>
                <Trans>Name</Trans>
                <MandatoryIndicator />

                <ValidatingInput
                    required
                    className="form-control"
                    type="text"
                    value={value.name || ''}
                    onChange={(e) => handleChange({ name: e.target.value })}
                    placeholder={t`Name`}
                />
            </label>

            <label>
                <Trans>Internal note</Trans>
                <InfoIconWithTooltip
                    content={
                        <Trans id="options.optionGroup.internalNote.tooltip">
                            A note shown only to sellers next to the option group name (e.g. "80cm oven"). Never visible to
                            buyers.
                        </Trans>
                    }
                />

                <ValidatingInput
                    className="form-control"
                    type="text"
                    value={value.internalNote || ''}
                    onChange={(e) => handleChange({ internalNote: e.target.value })}
                    placeholder={t`e.g. 80cm oven`}
                    maxLength={100}
                />
            </label>

            <label>
                <Trans>Description</Trans>

                {/* <MarkdownEditor markdown={value.description || ''} onChange={(e) => handleChange({ name: e })} fieldName='Description' /> */}
                <ValidatingTextArea
                    className="form-control"
                    value={value.description || ''}
                    onChange={(e) => handleChange({ description: e.target.value })}
                    placeholder={t`Name`}
                    minRows={6}
                    autoResize
                />
            </label>

            <label>
                <Trans>Deadline</Trans>
                <MandatoryIndicator />
                {disableEditDeadline && (
                    <AlertBanner type="warning">
                        <Trans>Deadline cannot be edited when option group has items linked to a style</Trans>
                    </AlertBanner>
                )}

                <ProjectDeadlineSelect
                    isDisabled={disableEditDeadline}
                    value={value.deadlineId || null}
                    onChange={(deadlineId) => handleChange({ deadlineId })}
                    projectId={projectId}
                    required
                />
            </label>

            <label>
                <Trans>Company profile</Trans>

                <LinkToUnitCompanyProfile
                    projectId={projectId}
                    onProvidedByProfileIdChange={(id) => handleChange({ providedByProfileId: id ? id : null })}
                    providedByProfileId={value.providedByProfileId || null}
                />
            </label>

            <label>
                <Trans>Fee set</Trans>
                <MandatoryIndicator />

                <ValidatingSelect
                    required
                    className="form-control"
                    value={value.projectFeeSetId}
                    onChange={(e) => handleChange({ projectFeeSetId: +e.target.value })}>
                    {feeSetQuery.data.map((x) => (
                        <option key={x.id} value={x.id}>
                            {x.name}
                        </option>
                    ))}
                </ValidatingSelect>
            </label>

            <label>
                <Trans>Tag</Trans>

                <ProjectTagSelect
                    projectId={projectId}
                    value={value.projectTagId ?? null}
                    onChange={(projectTagId) => handleChange({ projectTagId })}
                />
            </label>

            <Checkbox
                inputSize="md"
                checked={value.allowManualQuantity || false}
                onChange={(e) => handleChange({ allowManualQuantity: e.target.checked })}>
                <Trans>Allow buyer to choose quantity</Trans>
            </Checkbox>

            <Checkbox
                inputSize="md"
                checked={value.showQuantityToBuyers || false}
                onChange={(e) => handleChange({ showQuantityToBuyers: e.target.checked })}>
                <Trans>Show quantity to buyer</Trans>
            </Checkbox>

            <Checkbox
                inputSize="md"
                checked={value.disableManualSelection || false}
                onChange={(e) => handleChange({ disableManualSelection: e.target.checked })}>
                <Trans>Disable manual selection</Trans>
            </Checkbox>

            <Checkbox
                inputSize="md"
                checked={value.makeFree || false}
                onChange={(e) => handleChange({ makeFree: e.target.checked })}>
                <Trans>Make option free</Trans>
            </Checkbox>

            <Checkbox
                inputSize="md"
                checked={value.hidePrice || false}
                onChange={(e) => handleChange({ hidePrice: e.target.checked })}>
                <Trans>Hide prices</Trans>
            </Checkbox>
        </div>
    );
};
